import { NetworkStatus, useLazyQuery } from "@apollo/client";

import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";
import { GetEventsByModelAndFacilityParams } from "../../../../common/types/events/params";
import { RecordingsApiResponse } from "../../../../common/types/recordings/api";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { errorNotification } from "../../../../common/variables/notification";
import { useSelectedLocation } from "../../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../../common/variables/selectedModel";
import { GET_RECORDINGS_BY_MODEL_AND_FACILITY } from "../../../../rest/queries";
import { mapRecordingEntityToViewModel } from "../../adapters/recordingAdapter";
import { useState } from "react";

const DEFAULT_PAGE_SIZE = 500;

export const useGetRecordingsToExport = () => {
  const customerId = useCustomerIdGuard();
  const modelId = useSelectedModel();
  const facilityId = useSelectedLocation();

  const params: GetEventsByModelAndFacilityParams = {
    customerId,
    modelId: modelId?.value.toLowerCase() ?? "",
    facilityId: facilityId?.value ?? "",
    pageSize: DEFAULT_PAGE_SIZE,
  };

  const variables = buildEventQueryVariables(params);

  const [loading, setLoading] = useState(false);

  const [fetchRecordings, { error, fetchMore, networkStatus }] = useLazyQuery<{
    recordings: RecordingsApiResponse;
  }>(GET_RECORDINGS_BY_MODEL_AND_FACILITY, {
    variables: {
      ...variables,
      modelId: modelId?.value.toLowerCase() ?? "",
    },
    fetchPolicy: "cache-first",
    onError: error => {
      console.error("GetRecordingsByModelAndFacility:", error);

      errorNotification();
    },
  });

  const getRecordings = async () => {
    setLoading(true);

    try {
      const initialResponse = await fetchRecordings({
        variables: {
          ...variables,
          lastEvaluatedKey: "",
        },
      });

      let allRecordings = initialResponse.data?.recordings?.data ?? [];

      if (allRecordings.length === 0) {
        setLoading(false);

        errorNotification("No recordings found for export");

        return [];
      }

      let currentLastEvaluatedKey =
        initialResponse.data?.recordings?.lastEvaluatedKey;

      // Continue fetching until there are no more pages
      while (currentLastEvaluatedKey) {
        const moreResponse = await fetchMore({
          variables: {
            ...variables,
            pageSize: DEFAULT_PAGE_SIZE,
            lastEvaluatedKey: encodeURIComponent(
              JSON.stringify(currentLastEvaluatedKey)
            ),
          },
        });

        const newRecordings = moreResponse.data?.recordings?.data ?? [];

        allRecordings = [...allRecordings, ...newRecordings];

        currentLastEvaluatedKey =
          moreResponse.data?.recordings?.lastEvaluatedKey;
      }

      const mappedRecordings = allRecordings.map(mapRecordingEntityToViewModel);

      setLoading(false);

      return mappedRecordings;
    } catch (error) {
      console.error("Failed to fetch all recordings for export:", error);

      errorNotification("Failed to fetch recordings for export");

      setLoading(false);

      return [];
    }
  };

  return {
    getRecordings,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    error,
    fetchMore,
  } as const;
};
